// Shared tabbed per-language content editor. Each tab holds that language's
// TITLE + DESCRIPTION together, so switching the tab switches both at once.
// Replaces the stacked title inputs + description cards that lived on the staff
// zone/exhibit/exhibition forms.
//
// Casing-agnostic by design: it takes `titles`/`descriptions` maps + change
// callbacks rather than a raw response object, so a snake_case form
// (zone/exhibit) and a camelCase form (exhibition, ADR-0020) both drive it by
// mapping at the call site.

import type { FC } from "react";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@shared/components/ui/tabs";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { Textarea } from "@shared/components/ui/textarea";
import type { ContentLanguage } from "@shared/lib/language";

export interface LocalizedContentTabsProps {
  titles: Record<ContentLanguage, string>;
  descriptions: Record<ContentLanguage, string>;
  onTitleChange: (lang: ContentLanguage, value: string) => void;
  onDescriptionChange: (lang: ContentLanguage, value: string) => void;
  disabled?: boolean;
  /** Per-language description character cap + counter denominator. Default 2000. */
  maxLength?: number;
}

const LANGS: {
  lang: ContentLanguage;
  label: string;
  heading: string;
  required?: boolean;
}[] = [
  {
    lang: "tc",
    label: "TC",
    heading: "繁體中文 (Traditional Chinese)",
    required: true,
  },
  { lang: "en", label: "EN", heading: "English" },
  { lang: "sc", label: "SC", heading: "简体中文 (Simplified Chinese)" },
];

const LocalizedContentTabs: FC<LocalizedContentTabsProps> = ({
  titles,
  descriptions,
  onTitleChange,
  onDescriptionChange,
  disabled,
  maxLength = 2000,
}) => (
  <div
    className="space-y-4 p-6 bg-white border border-border rounded-lg"
    data-testid="content-tabs"
  >
    <h3 className="font-heading font-semibold">Title &amp; Description</h3>
    <Tabs defaultValue="tc">
      <TabsList data-testid="content-tabs-list">
        {LANGS.map(({ lang, label, required }) => {
          const filled =
            (titles[lang] ?? "").trim().length > 0 ||
            (descriptions[lang] ?? "").trim().length > 0;
          return (
            <TabsTrigger
              key={lang}
              value={lang}
              data-testid={`content-tab-${lang}`}
            >
              {label}
              {required ? " *" : ""}
              {filled && (
                <span
                  aria-hidden
                  className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-primary"
                  data-testid={`content-filled-${lang}`}
                />
              )}
            </TabsTrigger>
          );
        })}
      </TabsList>

      {LANGS.map(({ lang, heading, required }) => (
        <TabsContent key={lang} value={lang} className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor={`title_${lang}`}>
              Title — {heading}
              {required ? " *" : ""}
            </Label>
            <Input
              id={`title_${lang}`}
              value={titles[lang] ?? ""}
              onChange={(e) => onTitleChange(lang, e.target.value)}
              required={required}
              disabled={disabled}
              data-testid={`title-${lang}-input`}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor={`description_${lang}`}>
              Description (max {maxLength} characters){required ? " *" : ""}
            </Label>
            <Textarea
              id={`description_${lang}`}
              value={descriptions[lang] ?? ""}
              onChange={(e) => onDescriptionChange(lang, e.target.value)}
              maxLength={maxLength}
              rows={5}
              required={required}
              disabled={disabled}
              data-testid={`description-${lang}-input`}
            />
            <p className="text-xs text-muted-foreground">
              {(descriptions[lang] ?? "").length}/{maxLength} characters
            </p>
          </div>
        </TabsContent>
      ))}
    </Tabs>
  </div>
);

export default LocalizedContentTabs;
